import PropTypes from "prop-types";
import moment from "moment";

import { Button } from "@mui/material";
import Table from "@mui/material/Table";
import { TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import { FILE_UTILS } from "utils/files";
import { useRegenerateQAResume } from "@/hooks";
import client from "@/api";
import { LoadingOrContent } from "@/components/LoadingOrContent";

import styles from "./FilesSection.module.css";

const FilesSection = ({ files, submission, isLoadingCandidateFiles, handleRefreshFiles }) => {
  const data = files?.data?.files;

  const candidateID = submission.candidate.id;

  const { isLoading: isGeneratingQAResume, doRequest: generateQAResume } =
    useRegenerateQAResume(candidateID);

  const handleDownloadQAResume = async ({ fileID, fileName }) => {
    const response = await client.get(`/qa-resume/candidate/${candidateID}/file/${fileID}`, {
      responseType: "blob"
    });

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName.replaceAll(" ", "-");
    a.click();

    window.URL.revokeObjectURL(url);
  };

  /**
   * Generate QA Resume.
   */
  const handleRegenerateQAResume = async () => {
    const formattedDate = moment().format("M_D_YY");

    const data = {
      status: submission.status,
      customTextBlock1: submission.customTextBlock1,
      customTextBlock2: submission.customTextBlock2,
      customTextBlock3: submission.customTextBlock3,
      customDate2: moment(submission.customDate2).valueOf(),
      customDate1: moment(submission.customDate1).valueOf()
    };

    // Get Resume PDF file.
    const file = files?.data?.files.find((file) => file.type === "Resume");

    if (!file) {
      return;
    }

    // get Resume file.
    const response = await client.get(`/qa-resume/candidate/${candidateID}/file/${file.id}`, {
      responseType: "blob"
    });

    const blob = new Blob([response.data]);
    const resumeContent = await FILE_UTILS.transformBase64(blob);

    const params = {
      id: submission.id,
      jobId: submission.jobOrder.id,
      candidateId: candidateID,
      resumeContent,
      data,
      responses: data,
      jobTitle: submission.jobOrder.title,
      clientCorporation: {
        name: submission.jobOrder.clientCorporation.name
      },
      fileName: `${submission.candidate.firstName}${submission.candidate.lastName}_CV-job_${submission.jobOrder.id}-${submission.id}-${formattedDate}.pdf`
    };

    await generateQAResume(params);

    // refresh file list.
    handleRefreshFiles();
  };

  if (data?.length === 0) {
    return (
      <>
        <h1>Files</h1>
        <span>this Candidate has no files</span>
      </>
    );
  }

  return (
    <>
      <div className={styles["header-container"]}>
        <h1>Files</h1>

        <Button
          variant="contained"
          color="primary"
          size="medium"
          isLoading={isGeneratingQAResume}
          onClick={handleRegenerateQAResume}
        >
          Regenerate QA Resume
        </Button>
      </div>

      <LoadingOrContent isLoading={isLoadingCandidateFiles}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>type</TableCell>
              <TableCell>file</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map((row, index = 0) => (
              <TableRow key={`${index}-${row.id}`}>
                <TableCell component="th" scope="row">
                  {row.type}
                </TableCell>

                <TableCell component="th" scope="row">
                  <Button
                    color="inherit"
                    style={{ width: "100%", justifyContent: "space-between" }}
                    onClick={() => handleDownloadQAResume({ fileID: row.id, fileName: row.name })}
                  >
                    {row.name.replaceAll(" ", "-")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </LoadingOrContent>
    </>
  );
};

FilesSection.propTypes = {
  files: PropTypes.any,
  submission: PropTypes.any,
  isLoadingCandidateFiles: PropTypes.bool,
  candidateID: PropTypes.string,
  handleRefreshFiles: PropTypes.func
};

export { FilesSection };
